import * as z from "zod";

export const genders = ["boy", "girl", "neutral"] as const;
export type Gender = (typeof genders)[number];

export const genderSchema = z.enum(genders);

export const parseGender = (value: string): Gender => {
  const result = genderSchema.safeParse(value);
  return result.success ? result.data : "neutral";
};

export const nameRecordSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  gender: genderSchema,
  notes: z.string(),
  created_by: z.string().min(1),
  created_at: z.number().int(),
});
export type NameRecord = Readonly<z.infer<typeof nameRecordSchema>>;

export const newNameSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  gender: genderSchema,
  notes: z.string(),
});
export type NewName = Readonly<z.infer<typeof newNameSchema>>;

export const settingRecordSchema = z.object({
  id: z.string().min(1),
  key: z.string().min(1),
  value: z.string(),
  updated_by: z.string().min(1),
  updated_at: z.number().int(),
});
export type SettingRecord = Readonly<z.infer<typeof settingRecordSchema>>;

export const ratingScoreSchema = z.union([
  z.literal(-2),
  z.literal(-1),
  z.literal(0),
  z.literal(1),
  z.literal(2),
]);
export type RatingScore = z.infer<typeof ratingScoreSchema>;

export const ratingRecordSchema = z.object({
  id: z.string().min(1),
  name_id: z.string().min(1),
  user_id: z.string().min(1),
  score: ratingScoreSchema,
  reason: z.string(),
  updated_at: z.number().int(),
});
export type RatingRecord = Readonly<z.infer<typeof ratingRecordSchema>>;

export const newRatingSchema = z.object({
  name_id: z.string().min(1),
  user_id: z.string().min(1),
  score: ratingScoreSchema,
  reason: z.string(),
});
export type NewRating = Readonly<z.infer<typeof newRatingSchema>>;

export const ratingLabels: Readonly<Record<RatingScore, string>> = {
  [-2]: "NO",
  [-1]: "Dislike",
  [0]: "Neutral",
  [1]: "Sure",
  [2]: "LIKE",
};

export const statusValues = ["Pending", "Declined", "Match", "Mixed"] as const;
export type NameStatus = (typeof statusValues)[number];

export type NameWithRatings = {
  readonly name: NameRecord;
  readonly mine?: RatingRecord | undefined;
  readonly partner?: RatingRecord | undefined;
  readonly status: NameStatus;
};

export const statusColor: Readonly<
  Record<NameStatus, "default" | "success" | "error" | "warning">
> = {
  Pending: "default",
  Declined: "error",
  Match: "success",
  Mixed: "warning",
};
